import React from "react";
import { generatePath, Link, Navigate, useLocation } from "react-router-dom";

export function components<Path extends string, Params extends Record<string, Record<string, string | null>>>() {
  return {
    Link<P extends Path>(
      props: {
        to: P;
        search?: string;
        hash?: string;
        className?: string;
        children?: React.ReactNode;
        ["data-name"]?: string;
        experiments?: string[];
        onClick?: React.MouseEventHandler<HTMLAnchorElement>;
        target?: React.HTMLAttributeAnchorTarget;
        rel?: string;
        ["aria-label"]?: string;
        // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- {} means "no params required", not "empty object"
      } & (P extends keyof Params ? { params: Params[P] } : {}),
    ) {
      const path = generatePath(props.to, "params" in props ? props.params : undefined);
      const { to: _to, children, className, "data-name": dataName, experiments, search, hash, ...rest } = props;
      return (
        <Link
          to={{ pathname: path, search, hash }}
          className={className}
          data-name={dataName}
          data-experiments={experiments}
          {...rest}
        >
          {children}
        </Link>
      );
    },
    Navigate<P extends Path>(
      props: {
        to: P;
        hash?: string;
        replace?: boolean;
      } & ({ search?: string; preserveSearch?: false } | { search?: never; preserveSearch: true }) &
        // Tuple wrapping keeps the conditional non-distributive when JSX inference widens P.
        // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- {} means "no params required", not "empty object"
        ([P] extends [keyof Params] ? { params: Params[P] } : {}),
    ) {
      const path = generatePath(props.to, "params" in props ? props.params : undefined);
      const { search, hash, preserveSearch } = props;
      const currentLocation = useLocation();
      return (
        <Navigate
          to={{
            pathname: path,
            search: search ?? (preserveSearch ? currentLocation.search : undefined),
            hash,
          }}
          replace={props.replace}
        />
      );
    },
  };
}
